import {
  type CalendarDate,
  CalendarDateTime,
  fromDate,
  getLocalTimeZone,
  now,
  toCalendarDate,
  toCalendarDateTime,
  toZoned,
  ZonedDateTime,
} from "@internationalized/date";
import {
  type FieldApi,
  type ReactFormExtendedApi,
  useForm,
  useStore,
} from "@tanstack/react-form";
import { AlertCircleIcon, AlertTriangleIcon } from "lucide-react";
import {
  createContext,
  type ReactElement,
  useCallback,
  useContext,
  useMemo,
} from "react";

import { isTubeArchived } from "@blis/shared/schemas/tube";
import { type RouterOutputs, trpc } from "@blis/trpc/react";

import { ArchivedContainerBadge } from "@/components/ArchivedContainerBadge";
import {
  ComboBox,
  ComboBoxInput,
  ComboBoxItem,
  ComboBoxList,
} from "@/components/ui/combobox";
import { DatePicker, DatePickerInput } from "@/components/ui/date-picker";
import {
  DatePickerTimeZone,
  DatePickerWithZone,
} from "@/components/ui/date-picker-with-zone";
import { Input, TextField } from "@/components/ui/field";
import { Keyboard } from "@/components/ui/keyboard";
import { NumberField, NumberInput } from "@/components/ui/number-field";
import {
  Select,
  SelectItem,
  SelectList,
  SelectTrigger,
} from "@/components/ui/select";
import { Text } from "@/components/ui/text";
import { apiUtils } from "@/context/query";
import { type SampleTempType, TempUnit } from "@/lib/types";
import { usTimezones } from "@/lib/utils/tz";
import { usesEnumTempForSampleEdit } from "../../../lib/sample-temp-mode";
import { sampleTempTypeOptions } from "../../../lib/sample-temp-options";
import type { PreferredTube } from "../AccessionFormProvider";
import { FormField } from "../layout";

type TubeWithFields = RouterOutputs["samples"]["tubesWithFields"][number];
type Field = NonNullable<TubeWithFields["fieldSet"]>["fields"][number];
type FieldSpec = NonNullable<Field["fieldSpec"]>;
type SampleFieldBuiltIn = NonNullable<Field["builtIn"]>;

type FormContextValue = {
  form: ReactFormExtendedApi<SampleFormValues>;
  tubes: TubeWithFields[];
  existingSample?: SampleFormValues;
  hiddenFields: SampleFieldBuiltIn[];
};

export type SampleFieldValue = {
  fieldSpecId: number;
  textValue?: string | null;
  numericValue?: number | null;
  dateValue?: CalendarDate | null;
  booleanValue?: boolean | null;
  optionId?: number | null;
};

export type SampleFormValues = {
  tubeId: number;
  timeZone?: string | null;
  collection?: ZonedDateTime | null;
  collectionTimezone?: string | null;
  refrigerated?: ZonedDateTime | null;
  refrigeratedTimezone?: string | null;
  centrifuged?: ZonedDateTime | null;
  centrifugedTimezone?: string | null;
  quantity?: number | null;
  lot?: string | null;
  expiration?: CalendarDate | null;
  typeId?: string | null;
  transfusionId?: string | null;
  temp?: number;
  tempType?: SampleTempType;
  tempUnits?: TempUnit;
  fields?: SampleFieldValue[];
  [key: `field-${number}`]: SampleFieldValue | null | undefined;
};

const FormContext = createContext({} as FormContextValue);

export function SampleFormCustom({
  onSubmit,
  sample,
  preferredTube,
  hiddenFields,
  showReceivedTemp,
  packedAt,
}: {
  onSubmit: (values: SampleFormValues) => void;
  sample?: SampleFormValues;
  preferredTube?: PreferredTube;
  hiddenFields?: SampleFieldBuiltIn[];
  hideTimeZone?: boolean;
  showReceivedTemp?: boolean;
  packedAt?: Date | string | null;
}) {
  const { data } = trpc.samples.tubesWithFields.useQuery();

  // converts array of fields to a format a form can understand
  const fieldMap =
    sample?.fields?.reduce(
      (acc, field) => {
        acc[`field-${field.fieldSpecId}`] = field;
        return acc;
      },
      {} as Record<`field-${number}`, SampleFieldValue>,
    ) || {};

  const localTz = useMemo(() => getLocalTimeZone(), []);

  const form = useForm<SampleFormValues>({
    defaultValues: {
      fields: sample?.fields ?? [],
      ...sample,
      collectionTimezone: sample?.collectionTimezone,
      refrigeratedTimezone: sample?.refrigeratedTimezone,
      centrifugedTimezone: sample?.centrifugedTimezone,
      tubeId: preferredTube?.id ?? sample?.tubeId ?? Number.NaN,
      ...fieldMap,
    },
    onSubmit(props) {
      // convert to array of fields
      const fields = Object.entries(props.value)
        .filter(([key]) => key.startsWith("field-") && !!props.value[key])
        .map(([_, value]) => value) as SampleFieldValue[];

      onSubmit({ ...props.value, fields });
    },
  });

  return (
    <FormContext.Provider
      value={{
        form,
        tubes: data ?? [],
        existingSample: sample,
        hiddenFields: hiddenFields ?? [],
      }}
    >
      <form
        id="samples-form"
        className="flex flex-col gap-2 md:gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <TubeSelect />
        {/* {!hideTimeZone && <TimeZoneSelect />} */}
        <TubeFields />
        {showReceivedTemp ? (
          <SampleReceivedTempFields packedAt={packedAt} />
        ) : null}
      </form>
    </FormContext.Provider>
  );
}

const builtInToName: Record<SampleFieldBuiltIn, string> = {
  COLLECTION_DATETIME: "collection",
  REFRIGERATION_DATETIME: "refrigerated",
  CENTRIFUGATION_DATETIME: "centrifuged",
  LOT_NUMBER: "lot",
  LOT_EXPIRATION_DATE: "expiration",
  QUANTITY: "quantity",
  PATIENT_STATUS: "typeId",
  TRANSFUSION_STATUS: "transfusionId",
};

function TubeSelect() {
  const { tubes } = useContext(FormContext);

  const { form } = useContext(FormContext);

  return (
    <form.Field
      name="tubeId"
      validators={{
        onSubmit: createFieldValidator("tubeId", "Tube type", [
          requiredValidator(true),
        ]),
      }}
      listeners={{
        // reset the form when the tube is changed, maintaining appropriate fields
        onChange: ({ value, fieldApi }) => {
          const { tubeId, ...previousValues } = fieldApi.form.state.values;
          const prevKeys = Object.keys(previousValues);

          const tube = tubes.find((t) => t.id === value);
          const fields = tube?.fieldSet?.fields || [];

          const keep: Record<string, boolean> = { timeZone: true };
          const newValues: Record<string, unknown> = {};

          for (const field of fields) {
            if (field.builtIn) {
              keep[builtInToName[field.builtIn]] = true;
            } else if (field.fieldSpec) {
              keep[`field-${field.fieldSpec.id}`] = true;
            }
          }

          for (const key of prevKeys) {
            newValues[key] = keep[key] ? previousValues[key] : null;
          }

          fieldApi.form.reset({
            tubeId: value,
            ...newValues,
            timeZone: fieldApi.form.getFieldValue("timeZone"),
            collectionTimezone:
              fieldApi.form.getFieldValue("collectionTimezone"),
            refrigeratedTimezone: fieldApi.form.getFieldValue(
              "refrigeratedTimezone",
            ),
            centrifugedTimezone: fieldApi.form.getFieldValue(
              "centrifugedTimezone",
            ),
          });
        },
      }}
    >
      {({ handleChange, handleBlur, name, state }) => {
        const selectableTubes = tubes.filter(
          (tube) => !isTubeArchived(tube) || tube.id === state.value,
        );

        return (
          <FormField
            title="Tube type"
            error={state.meta.errors.join(", ")}
            className="flex-nowrap"
          >
            <Select
              id={name}
              name={name}
              aria-label="Tube type"
              selectedKey={state.value || null}
              onSelectionChange={(key) => {
                handleChange(key as number);
              }}
              onBlur={handleBlur}
              isInvalid={!!state.meta.errors.length}
              validationBehavior="aria"
              className="min-w-48 max-w-56"
              placeholder="Select tube type"
            >
              <SelectTrigger />
              <SelectList items={selectableTubes}>
                {(tube) => (
                  <SelectItem key={tube.id} id={tube.id} textValue={tube.name}>
                    <span className="flex items-center gap-2">
                      {tube.name}
                      {isTubeArchived(tube) ? <ArchivedContainerBadge /> : null}
                    </span>
                  </SelectItem>
                )}
              </SelectList>
            </Select>
          </FormField>
        );
      }}
    </form.Field>
  );
}

function TimeZoneSelect() {
  const { form, tubes } = useContext(FormContext);
  const tubeId = useStore(form.store, (s) => s.values.tubeId);
  const tube = tubes.find((t) => t.id === tubeId);

  const hasTimeFields = tube?.fieldSet?.fields.some(
    (f) =>
      [
        "COLLECTION_DATETIME",
        "REFRIGERATION_DATETIME",
        "CENTRIFUGATION_DATETIME",
      ].includes(f.builtIn ?? "") || f.fieldSpec?.type === "DATETIME",
  );

  return hasTimeFields ? (
    <form.Field
      name="timeZone"
      validators={{
        onSubmit: createFieldValidator("timeZone", "Time zone", [
          requiredValidator(true),
        ]),
      }}
      listeners={{
        onChange: ({ value: tz, fieldApi }) => {
          if (!tz) return;

          const values = fieldApi.form.state.values;
          for (const [key, value] of Object.entries(values)) {
            if (value instanceof ZonedDateTime) {
              const newValue = toCalendarDateTime(value);
              const newZoned = toZoned(newValue, tz);
              fieldApi.form.setFieldValue(
                key as keyof SampleFormValues,
                newZoned,
              );
            }
          }
        },
      }}
    >
      {({ state, handleChange, handleBlur, name }) => (
        <FormField
          title="Time zone"
          error={state.meta.errors.join(", ")}
          className="flex-nowrap"
        >
          <Select
            id={name}
            name={name}
            aria-label="Time zone"
            selectedKey={state.value || null}
            onSelectionChange={(key) => {
              handleChange(key as string);
            }}
            onBlur={handleBlur}
            isInvalid={!!state.meta.errors.length}
            validationBehavior="aria"
            className="min-w-48 max-w-56"
            placeholder="Select time zone"
          >
            <SelectTrigger />
            <SelectList items={usTimezones}>
              {(timeZone) => (
                <SelectItem
                  key={timeZone.zone}
                  id={timeZone.zone}
                  textValue={timeZone.abbr}
                >
                  <div className="flex items-center justify-between w-full gap-2">
                    <span>{timeZone.display}</span>
                    <span className="text-xs text-muted-foreground">
                      {timeZone.abbr}
                    </span>
                  </div>
                </SelectItem>
              )}
            </SelectList>
          </Select>
        </FormField>
      )}
    </form.Field>
  ) : null;
}

function TubeFields() {
  const { form, tubes } = useContext(FormContext);

  const tubeId = useStore(form.store, (s) => s.values.tubeId);
  const tube = tubes.find((t) => t.id === tubeId);

  return (
    <>
      {tube?.fieldSet?.fields.map((field) => {
        if (field.builtIn) {
          return (
            <SampleBuiltInField
              key={field.id}
              // biome-ignore lint/style/noNonNullAssertion: checked above
              builtIn={field.builtIn!}
              required={field.required}
            />
          );
        }
        if (field.fieldSpec) {
          return (
            <SampleField
              key={field.id}
              spec={field.fieldSpec}
              required={field.required}
            />
          );
        }
        return null;
      })}
    </>
  );
}

function SampleBuiltInField({
  builtIn,
  required,
}: {
  builtIn: SampleFieldBuiltIn;
  required: boolean;
}) {
  const { form, existingSample, hiddenFields } = useContext(FormContext);
  const patientStatuses = trpc.samples.tubeTypes.useQuery();
  const transfusions = trpc.samples.transfusions.useQuery();

  const copyCollection = useCallback(
    (field: "refrigerated" | "centrifuged") =>
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key.toLowerCase() === "c") {
          e.stopPropagation();
          const collection = form.getFieldValue("collection");
          if (collection) {
            form.setFieldValue(field, collection);
            if (field === "refrigerated") {
              form.setFieldValue(
                "refrigeratedTimezone",
                form.getFieldValue("collectionTimezone"),
              );
            } else if (field === "centrifuged") {
              form.setFieldValue(
                "centrifugedTimezone",
                form.getFieldValue("collectionTimezone"),
              );
            }
          }
        }
      },
    [form],
  );

  if (hiddenFields.includes(builtIn)) {
    return null;
  }

  switch (builtIn) {
    case "COLLECTION_DATETIME":
      return (
        <form.Field
          name="collection"
          validators={{
            onSubmit: createFieldValidator("collection", "Collection", [
              requiredValidator(true),
              dateNotFutureValidator(),
            ]),
          }}
          listeners={{
            onChange: ({ value, fieldApi }) => {
              const tz = fieldApi.form.getFieldValue("timeZone");
              if (value && tz) {
                const newZoned = toZoned(value, tz);
                fieldApi.form.setFieldValue("collection", newZoned);
              }
            },
          }}
        >
          {({ state, handleChange, handleBlur, name }) => (
            <FormField
              title="Collection"
              error={state.meta.errors.join(", ")}
              className="flex-nowrap"
              helpText={
                <div className="flex flex-col gap-1">
                  <Text size="xs">
                    <Keyboard keys="T" /> Today's date
                  </Text>
                  <Text size="xs">
                    <Keyboard keys="Y" /> Yesterday's date
                  </Text>
                </div>
              }
            >
              <div className="flex items-center gap-1">
                <DatePickerWithZone
                  id={name}
                  name={name}
                  aria-label="Collection date"
                  className="min-w-48 flex-row"
                  granularity="minute"
                  value={state.value ?? null}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={state.meta.errors.length > 0}
                  validationBehavior="aria"
                >
                  <DatePickerInput />
                  <DatePickerTimeZone />
                </DatePickerWithZone>
              </div>
            </FormField>
          )}
        </form.Field>
      );
    case "REFRIGERATION_DATETIME":
      return (
        <form.Field
          name="refrigerated"
          validators={{
            onSubmit: createFieldValidator("refrigerated", "Refrigeration", [
              requiredValidator(required),
              dateNotFutureValidator(),
              notBeforeCollectionValidator(),
            ]),
          }}
        >
          {({ state, handleChange, handleBlur, name }) => (
            <FormField
              title="Refrigerated"
              error={state.meta.errors.join(", ")}
              className="flex-nowrap"
              helpText={
                <div className="flex flex-col gap-1">
                  <Text size="xs">
                    <Keyboard keys="C" /> Copy collection time
                  </Text>
                  <Text size="xs">
                    <Keyboard keys="T" /> Today's date
                  </Text>
                  <Text size="xs">
                    <Keyboard keys="Y" /> Yesterday's date
                  </Text>
                </div>
              }
            >
              <div className="flex items-center gap-1">
                <DatePickerWithZone
                  id={name}
                  name={name}
                  aria-label="Refrigeration date"
                  className="min-w-48 flex-row"
                  granularity="minute"
                  value={state.value ?? null}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onKeyDown={copyCollection("refrigerated")}
                  isInvalid={state.meta.errors.length > 0}
                  validationBehavior="aria"
                >
                  <DatePickerInput />
                  <DatePickerTimeZone />
                </DatePickerWithZone>
              </div>
            </FormField>
          )}
        </form.Field>
      );
    case "CENTRIFUGATION_DATETIME":
      return (
        <form.Field
          name="centrifuged"
          validators={{
            onSubmit: createFieldValidator("centrifuged", "Centrifuge", [
              requiredValidator(required),
              dateNotFutureValidator(),
              notBeforeCollectionValidator(),
            ]),
          }}
          listeners={{
            onChange: ({ value, fieldApi }) => {
              const tz = fieldApi.form.getFieldValue("timeZone");
              if (value && tz) {
                const newZoned = toZoned(value, tz);
                fieldApi.form.setFieldValue("centrifuged", newZoned);
              }
            },
          }}
        >
          {({ state, handleChange, handleBlur, name }) => (
            <FormField
              title="Centrifuged"
              error={state.meta.errors.join(", ")}
              className="flex-nowrap"
              helpText={
                <div className="flex flex-col gap-1">
                  <Text size="xs">
                    <Keyboard keys="C" /> Copy collection time
                  </Text>
                  <Text size="xs">
                    <Keyboard keys="T" /> Today's date
                  </Text>
                  <Text size="xs">
                    <Keyboard keys="Y" /> Yesterday's date
                  </Text>
                </div>
              }
            >
              <div className="flex items-center gap-1">
                <DatePickerWithZone
                  id={name}
                  name={name}
                  aria-label="Centrifuge date"
                  className="min-w-48 flex-row"
                  granularity="minute"
                  value={state.value ?? null}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onKeyDown={copyCollection("centrifuged")}
                  isInvalid={state.meta.errors.length > 0}
                  validationBehavior="aria"
                >
                  <DatePickerInput />
                  <DatePickerTimeZone />
                </DatePickerWithZone>
              </div>
            </FormField>
          )}
        </form.Field>
      );
    case "LOT_NUMBER":
      return (
        <form.Field
          name="lot"
          validators={{
            onSubmit: createFieldValidator("lot", "Lot number", [
              requiredValidator(required),
            ]),
          }}
          listeners={{
            onBlur: ({ value, fieldApi }) => {
              const info = fieldApi.form.getFieldInfo("expiration");
              const hasExpirationField = !!info?.instance;
              const expirationValue = fieldApi.form.getFieldValue("expiration");
              if (value && hasExpirationField && !expirationValue) {
                apiUtils.samples.byLotNumber
                  .fetch({ lot: value as string })
                  .then((res) => {
                    if (res?.expiration) {
                      fieldApi.form.setFieldValue(
                        "expiration",
                        toCalendarDate(fromDate(res.expiration, "UTC")),
                      );
                    }
                  });
              }
            },
          }}
        >
          {({ state, handleChange, handleBlur, name }) => (
            <FormField
              title="Lot"
              error={state.meta.errors.join(", ")}
              className="flex-nowrap"
            >
              <TextField
                id={name}
                name={name}
                aria-label="Lot number"
                className="w-36"
                value={state.value || ""}
                onChange={(val) => {
                  handleChange(val);
                }}
                onBlur={handleBlur}
                isInvalid={state.meta.errors.length > 0}
                validationBehavior="aria"
              >
                <Input />
              </TextField>
            </FormField>
          )}
        </form.Field>
      );
    case "LOT_EXPIRATION_DATE":
      return <LotExpiration required={required} />;
    case "QUANTITY":
      return (
        <form.Field
          name="quantity"
          defaultValue={existingSample?.quantity ?? 1}
          validators={{
            onSubmit: createFieldValidator("quantity", "Quantity", [
              requiredValidator(required),
            ]),
          }}
        >
          {({ state, handleChange, handleBlur, name }) => (
            <FormField
              title="Quantity"
              error={state.meta.errors?.join(", ")}
              className="flex-nowrap"
            >
              <NumberField
                id={name}
                name={name}
                aria-label="Quantity"
                className="w-36"
                value={state.value || Number.NaN}
                onChange={(val) => {
                  handleChange(val);
                }}
                onBlur={handleBlur}
                minValue={1}
                isInvalid={state.meta.errors?.length > 0}
                validationBehavior="aria"
              >
                <NumberInput />
              </NumberField>
            </FormField>
          )}
        </form.Field>
      );
    case "PATIENT_STATUS":
      return (
        <form.Field
          name="typeId"
          validators={{
            onSubmit: createFieldValidator("typeId", "Patient status", [
              requiredValidator(required),
            ]),
          }}
        >
          {({ state, handleChange, handleBlur, name }) => (
            <FormField
              title="Status"
              error={state.meta.errors?.join(", ")}
              className="flex-nowrap"
            >
              <Select
                aria-label="Patient status"
                selectedKey={state.value ?? null}
                onSelectionChange={(val) => handleChange(val as string)}
                isInvalid={state.meta.errors?.length > 0}
                onBlur={handleBlur}
                validationBehavior="aria"
                className="min-w-48 max-w-56"
              >
                <SelectTrigger />
                <SelectList items={patientStatuses.data ?? []}>
                  {(item) => (
                    <SelectItem
                      key={item.value}
                      id={item.value}
                      value={item}
                      textValue={item.description}
                    >
                      {item.description}
                    </SelectItem>
                  )}
                </SelectList>
              </Select>
            </FormField>
          )}
        </form.Field>
      );
    case "TRANSFUSION_STATUS":
      return (
        <form.Field
          name="transfusionId"
          validators={{
            onSubmit: createFieldValidator("transfusionId", "Transfusion", [
              requiredValidator(required),
            ]),
          }}
        >
          {({ state, handleChange, handleBlur, name }) => (
            <FormField
              title="Transfusion"
              error={state.meta.errors?.join(", ")}
              className="flex-nowrap"
            >
              <Select
                aria-label="Transfusion"
                selectedKey={state.value ?? null}
                onSelectionChange={(val) => handleChange(val as string)}
                isInvalid={state.meta.errors?.length > 0}
                onBlur={handleBlur}
                validationBehavior="aria"
                className="min-w-48 max-w-56"
              >
                <SelectTrigger />
                <SelectList items={transfusions.data ?? []}>
                  {(item) => (
                    <SelectItem
                      key={item.value}
                      id={item.value}
                      value={item}
                      textValue={item.description}
                    >
                      {["Pre", "Post"].includes(item.description)
                        ? `${item.description}-Transfusion`
                        : item.description}
                    </SelectItem>
                  )}
                </SelectList>
              </Select>
            </FormField>
          )}
        </form.Field>
      );
  }
  return null;
}

function LotExpiration({ required }: { required: boolean }) {
  const { form } = useContext(FormContext);
  return (
    <form.Field
      name="expiration"
      validators={{
        onSubmit: createFieldValidator("expiration", "Lot expiration", [
          requiredValidator(required),
        ]),
      }}
    >
      {({ state, handleChange, handleBlur, name }) => (
        <FormField
          title="Expiration"
          error={state.meta.errors.join(", ")}
          className="flex-nowrap"
        >
          <DatePicker
            id={name}
            name={name}
            aria-label="Lot expiration date"
            className="w-36"
            granularity="day"
            value={state.value ?? null}
            onChange={handleChange}
            onBlur={handleBlur}
            isInvalid={state.meta.errors.length > 0}
            validationBehavior="aria"
          >
            <DatePickerInput />
          </DatePicker>
          <form.Subscribe
            selector={(state) =>
              [
                state.values.expiration,
                state.isBlurred,
                state.isTouched,
                state.isPristine,
              ] as const
            }
          >
            {([expiration, _isBlurred, _isTouched, _isPristine]) => {
              let expiredEl: ReactElement | null = null;
              if (!expiration) {
                return null;
              }
              const daysToExpiration = expiration.compare(
                now(getLocalTimeZone()),
              );

              // magic number alert - this is so the warning isn't shown on a partial date
              if (daysToExpiration < -600000) {
                return null;
              }

              if (daysToExpiration < 0) {
                expiredEl = (
                  <span className="flex items-center gap-1 text-red-500">
                    <AlertCircleIcon className="size-3" /> Lot is expired
                  </span>
                );
              } else if (daysToExpiration <= 7) {
                expiredEl = (
                  <span className="flex items-center gap-1 text-yellow-600">
                    <AlertTriangleIcon className="size-3" /> Lot expires in{" "}
                    {daysToExpiration} days
                  </span>
                );
              }
              if (!expiredEl) {
                return null;
              }
              return (
                <div className="flex justify-end w-full text-xs mt-1">
                  {expiredEl}
                </div>
              );
            }}
          </form.Subscribe>
        </FormField>
      )}
    </form.Field>
  );
}

function SampleField({
  spec,
  required,
}: {
  spec: FieldSpec;
  required: boolean;
}) {
  const { form } = useContext(FormContext);

  switch (spec.type) {
    case "TEXT":
      return (
        <form.Field
          name={`field-${spec.id}`}
          validators={{
            onSubmit({ value }) {
              return required && !value
                ? `${spec.name} is required`
                : undefined;
            },
          }}
        >
          {({ state, handleChange, handleBlur, name }) => (
            <FormField
              title={spec.name}
              error={state.meta.errors.join(", ")}
              className="flex-nowrap"
            >
              <TextField
                id={name}
                name={name}
                aria-label={spec.name}
                value={state.value?.textValue || ""}
                onChange={(val) => {
                  handleChange({
                    fieldSpecId: spec.id,
                    textValue: val,
                  });
                }}
                onBlur={handleBlur}
                isInvalid={state.meta.errors.length > 0}
                validationBehavior="aria"
              >
                <Input />
              </TextField>
            </FormField>
          )}
        </form.Field>
      );
    case "NUMBER":
      return (
        <form.Field
          name={`field-${spec.id}`}
          validators={{
            onSubmit({ value }) {
              return required && !value
                ? `${spec.name} is required`
                : undefined;
            },
          }}
        >
          {({ state, handleChange, handleBlur, name }) => (
            <FormField
              title={spec.name}
              error={state.meta.errors.join(", ")}
              className="flex-nowrap"
            >
              <NumberField
                id={name}
                name={name}
                aria-label={spec.name}
                value={state.value?.numericValue || Number.NaN}
                onChange={(val) => {
                  handleChange({
                    fieldSpecId: spec.id,
                    numericValue: val,
                  });
                }}
                onBlur={handleBlur}
                isInvalid={state.meta.errors.length > 0}
                validationBehavior="aria"
              >
                <NumberInput />
              </NumberField>
            </FormField>
          )}
        </form.Field>
      );
    case "DATE":
      return (
        <form.Field
          name={`field-${spec.id}`}
          validators={{
            onSubmit({ value }) {
              return required && !value
                ? `${spec.name} is required`
                : undefined;
            },
          }}
        >
          {({ state, handleChange, handleBlur, name }) => (
            <FormField
              title={spec.name}
              error={state.meta.errors.join(", ")}
              className="flex-nowrap"
            >
              <DatePicker
                id={name}
                name={name}
                aria-label={spec.name}
                granularity="day"
                value={state.value?.dateValue || null}
                onChange={(val) => {
                  handleChange({
                    fieldSpecId: spec.id,
                    dateValue: val,
                  });
                }}
                onBlur={handleBlur}
                isInvalid={state.meta.errors.length > 0}
                validationBehavior="aria"
              >
                <DatePickerInput />
              </DatePicker>
            </FormField>
          )}
        </form.Field>
      );
    case "DATETIME":
      return (
        <form.Field
          name={`field-${spec.id}`}
          validators={{
            onSubmit({ value }) {
              return required && !value
                ? `${spec.name} is required`
                : undefined;
            },
          }}
        >
          {({ state, handleChange, handleBlur, name }) => (
            <FormField
              title={spec.name}
              error={state.meta.errors.join(", ")}
              className="flex-nowrap"
            >
              <DatePicker
                id={name}
                name={name}
                aria-label={spec.name}
                granularity="minute"
                value={state.value?.dateValue || null}
                onChange={(val) => {
                  handleChange({
                    fieldSpecId: spec.id,
                    dateValue: val,
                  });
                }}
                onBlur={handleBlur}
                isInvalid={state.meta.errors.length > 0}
                validationBehavior="aria"
              >
                <DatePickerInput />
              </DatePicker>
            </FormField>
          )}
        </form.Field>
      );
    case "SELECT":
      if (spec.allowFreeText) {
        return (
          <form.Field
            name={`field-${spec.id}`}
            validators={{
              onSubmit({ value }) {
                return required && !value
                  ? `${spec.name} is required`
                  : undefined;
              },
            }}
          >
            {({ state, handleChange, handleBlur, name }) => (
              <FormField
                title={spec.name}
                error={state.meta.errors.join(", ")}
                className="flex-nowrap"
              >
                <ComboBox
                  id={name}
                  name={name}
                  aria-label={spec.name}
                  allowsCustomValue
                  menuTrigger="focus"
                  selectedKey={state.value?.optionId || null}
                  onSelectionChange={(val) => {
                    handleChange(
                      !val
                        ? null
                        : {
                            fieldSpecId: spec.id,
                            optionId: val as number,
                          },
                    );
                  }}
                  // onBlur={handleBlur}
                  inputValue={
                    state.value?.optionId
                      ? spec.options.find((o) => o.id === state.value?.optionId)
                          ?.value
                      : state.value?.textValue || ""
                  }
                  onInputChange={(val) => {
                    handleChange({
                      fieldSpecId: spec.id,
                      textValue: val,
                    });
                  }}
                  className="min-w-48 max-w-56"
                  isInvalid={state.meta.errors.length > 0}
                  validationBehavior="aria"
                >
                  <ComboBoxInput placeholder="Select or enter" />
                  <ComboBoxList items={spec.options}>
                    {(option) => (
                      <ComboBoxItem key={option.id} id={option.id}>
                        {option.value}
                      </ComboBoxItem>
                    )}
                  </ComboBoxList>
                </ComboBox>
              </FormField>
            )}
          </form.Field>
        );
      }
      return (
        <form.Field
          name={`field-${spec.id}`}
          validators={{
            onSubmit({ value }) {
              return required && !value?.optionId
                ? `${spec.name} is required`
                : undefined;
            },
          }}
        >
          {({ state, handleChange, handleBlur, name }) => (
            <FormField
              title={spec.name}
              error={state.meta.errors.join(", ")}
              className="flex-nowrap"
            >
              <Select
                id={name}
                name={name}
                aria-label={spec.name}
                selectedKey={state.value?.optionId || null}
                onSelectionChange={(key) => {
                  handleChange(
                    !key
                      ? null
                      : {
                          fieldSpecId: spec.id,
                          optionId: key as number,
                        },
                  );
                }}
                onBlur={handleBlur}
                isInvalid={state.meta.errors.length > 0}
                validationBehavior="aria"
                className="min-w-48 max-w-56"
              >
                <SelectTrigger isClearable={!required} />
                <SelectList items={spec.options}>
                  {(option) => (
                    <SelectItem key={option.id} id={option.id}>
                      {option.value}
                    </SelectItem>
                  )}
                </SelectList>
              </Select>
            </FormField>
          )}
        </form.Field>
      );
  }

  return <div>{spec.name}</div>;
}

function SampleReceivedTempFields({
  packedAt,
}: {
  packedAt?: Date | string | null;
}) {
  const { form, existingSample } = useContext(FormContext);
  const useEnumTemp = usesEnumTempForSampleEdit(
    {
      temp: existingSample?.temp,
      tempType: existingSample?.tempType,
    },
    packedAt,
  );

  if (useEnumTemp) {
    return (
      <form.Field
        name="tempType"
        validators={{
          onSubmit: createFieldValidator("tempType", "Received temperature", [
            requiredValidator(true),
          ]),
        }}
      >
        {({ state, handleChange, handleBlur, name }) => (
          <FormField
            title="Received temperature"
            error={state.meta.errors.join(", ")}
            className="flex-nowrap"
          >
            <Select
              id={name}
              name={name}
              aria-label="Received temperature"
              selectedKey={state.value ?? null}
              onSelectionChange={(key) => {
                handleChange(key as SampleTempType);
              }}
              onBlur={handleBlur}
              isInvalid={state.meta.errors.length > 0}
              validationBehavior="aria"
              className="min-w-48 max-w-56"
            >
              <SelectTrigger />
              <SelectList>
                {sampleTempTypeOptions.map((option) => (
                  <SelectItem
                    key={option.value}
                    id={option.value}
                    textValue={option.label.split(" ")[0]}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectList>
            </Select>
          </FormField>
        )}
      </form.Field>
    );
  }

  return (
    <form.Field
      name="temp"
      validators={{
        onSubmit: createFieldValidator("temp", "Received temperature", [
          numericRequiredValidator(),
        ]),
      }}
    >
      {({ state, handleChange, handleBlur, name }) => (
        <FormField
          title="Received temperature"
          error={state.meta.errors.join(", ")}
          className="flex-nowrap"
        >
          <NumberField
            id={name}
            name={name}
            aria-label="Received temperature"
            value={state.value ?? Number.NaN}
            onChange={(val) => {
              handleChange(val);
              form.setFieldValue("tempUnits", TempUnit.C);
            }}
            onBlur={handleBlur}
            isInvalid={state.meta.errors.length > 0}
            validationBehavior="aria"
            className="min-w-48 max-w-56"
          >
            <NumberInput />
          </NumberField>
        </FormField>
      )}
    </form.Field>
  );
}

type FieldValidator<K extends keyof SampleFormValues> = (props: {
  name: K;
  label: string;
  value: SampleFormValues[K];
  fieldApi: FieldApi<SampleFormValues, K>;
}) => string | undefined;

/**
 * Creates onSubmit validator for a field based on a sequence of custom validators.
 */
function createFieldValidator<K extends keyof SampleFormValues>(
  name: K,
  label: string,
  validators: FieldValidator<K>[],
) {
  return ({ value, fieldApi }) => {
    for (const validator of validators) {
      const result = validator({ name, label, value, fieldApi });
      if (result) return result;
    }
    return undefined;
  };
}

function requiredValidator(
  required: boolean,
): FieldValidator<keyof SampleFormValues> {
  return ({ label, value }) => {
    return required && !value ? `${label} is required` : undefined;
  };
}

function numericRequiredValidator(): FieldValidator<"temp"> {
  return ({ label, value }) => {
    return typeof value !== "number" || Number.isNaN(value)
      ? `${label} is required`
      : undefined;
  };
}

function dateNotFutureValidator(): FieldValidator<keyof SampleFormValues> {
  return ({ label, value }) => {
    if (typeof value !== "object") return undefined;

    if (value instanceof CalendarDateTime || value instanceof ZonedDateTime) {
      const rightNow = now(getLocalTimeZone());
      return value.compare(rightNow) > 0
        ? `${label} cannot be in the future`
        : undefined;
    }
    return undefined;
  };
}

function notBeforeCollectionValidator(): FieldValidator<
  keyof SampleFormValues
> {
  return ({ label, value, fieldApi }) => {
    const collection = fieldApi.form.getFieldValue("collection");
    if (!collection) return undefined;
    if (!value || typeof value !== "object") return undefined;

    if (value instanceof CalendarDateTime || value instanceof ZonedDateTime) {
      return value.compare(collection) < 0
        ? `${label} must be after collection date`
        : undefined;
    }

    return undefined;
  };
}
